import {
    DropdownItem,
    ExpiringLevelTermSummary,
    LapseProjectionSummary,
    MajorPolicyLoanSummary,
    PolicyBreakdown,
    PolicyExpiringLevelTermData,
    PolicyLapseProjectionData,
    PolicyLoanData,
    TrustBreakdown
} from "../types";
import React, { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from "react";
import { createExpiringLevelTermPolicies, createLapseProjectionSummary, createMajorPolicyLoans } from "../services/PolicyLapseDataService";
import { filterSelector, getPortfolioListOptions } from "../services/Lib";

import { IIQContext } from "./IIQContextProvider";
import SimpleLoader from "./SimpleLoader";
import { TrustData } from "../features/TrustDetails/Types";
import { policyDataBreakdown as createPolicyBreakdown } from "../services/PolicyDataService";
import { createTrustBreakdown } from "../services/TrustDataService";
import useSproc from "../hooks/useSproc";

interface PortfolioContextProps {
    trustData: TrustData[];
    policyData: PolicyData[];
    policyLapseProjectionData: PolicyLapseProjectionData[];
    lapseProjectionSummary: LapseProjectionSummary[];
    policyBreakdown: PolicyBreakdown[];
    trustBreakdown: TrustBreakdown[];
    documentApprovalQueue: DocumentApprovalData[];
    documentApprovalCount: number;
    policyExpiringLevelTermData: PolicyExpiringLevelTermData[];
    expiringLevelTermPolicies: ExpiringLevelTermSummary[];
    policyLoanData: PolicyLoanData[];
    majorPolicyLoans: MajorPolicyLoanSummary[];
    loading: boolean;
    error: boolean;
    selectedClient?: number;
    setSelectedClient: (id?: number) => void;
    selectedTRM?: number;
    setSelectedTRM: (id?: number) => void;
    selectedTrustAdvisor?: number;
    setSelectedTrustAdvisor: (id?: number) => void;
    selectedTrustAdministrator?: number;
    setSelectedTrustAdministrator: (id?: number) => void;
    selectedClientAdvisor?: number;
    setSelectedClientAdvisor: (id?: number) => void;
    setTrustData: (data: TrustData[]) => void;
    clientOptions: DropdownItem[];
    trmOptions: DropdownItem[];
    trustAdvisorOptions: DropdownItem[];
    trustAdministratorOptions: DropdownItem[];
    clientAdvisorOptions: DropdownItem[];
    onDocumentsApprovalQueueChange: (value: DocumentApprovalData[]) => void;
    unreadNotifications: number;
    setUnreadNotifications: (x: number) => void;
    setPolicyData: Dispatch<SetStateAction<PolicyData[]>>;
}

export const PortfolioContext = createContext<PortfolioContextProps>({
    trustData: [],
    policyData: [],
    policyLapseProjectionData: [],
    lapseProjectionSummary: [],
    policyBreakdown: [],
    trustBreakdown: [],
    documentApprovalQueue: [],
    documentApprovalCount: 0,
    policyExpiringLevelTermData: [],
    expiringLevelTermPolicies: [],
    policyLoanData: [],
    majorPolicyLoans: [],
    loading: true,
    error: false,
    clientOptions: [],
    trmOptions: [],
    trustAdvisorOptions: [],
    trustAdministratorOptions: [],
    clientAdvisorOptions: [],
    setSelectedClient: () => {
        throw new Error("Not implemented");
    },
    setSelectedTRM: () => {
        throw new Error("Not implemented");
    },
    setSelectedTrustAdvisor: () => {
        throw new Error("Not implemented");
    },
    setSelectedTrustAdministrator: () => {
        throw new Error("Not implemented");
    },
    setSelectedClientAdvisor: () => {
        throw new Error("Not implemented");
    },
    setTrustData: () => {
        throw new Error("Not implemented");
    },
    onDocumentsApprovalQueueChange: () => {
        throw new Error("Not implemented");
    },
    unreadNotifications: 0,
    setUnreadNotifications: () => {
        throw new Error("Not implemented");
    },
    setPolicyData: () => {
        throw new Error("Not Implemented");
    }
});

interface PortfolioProviderProps {
    children: React.ReactNode;
}

export const PortfolioProvider: React.FC<PortfolioProviderProps> = (props: PortfolioProviderProps) => {
    const { children } = props;
    const { appData } = useContext(IIQContext);
    const sproc = useSproc();

    const [trustData, setTrustData] = useState<TrustData[]>([]);
    const [policyLoanData, setPolicyLoanData] = useState<PolicyLoanData[]>([]);
    const [majorPolicyLoans, setMajorPolicyLoans] = useState<MajorPolicyLoanSummary[]>([]);
    const [policyExpiringLevelTermData, setPolicyExpiringLevelTermData] = useState<PolicyExpiringLevelTermData[]>([]);
    const [expiringLevelTermPolicies, setExpiringLevelTermPolicies] = useState<ExpiringLevelTermSummary[]>([]);
    const [policyData, setPolicyData] = useState<PolicyData[]>([]);
    const [policyLapseProjectionData, setPolicyLapseProjectionData] = useState<PolicyLapseProjectionData[]>([]);
    const [lapseProjectionSummary, setLapseProjectionSummary] = useState<LapseProjectionSummary[]>([]);
    const [policyBreakdown, setPolicyBreakdown] = useState<PolicyBreakdown[]>([]);
    const [trustBreakdown, setTrustBreakdown] = useState<TrustBreakdown[]>([]);
    const [documentApprovalData, setDocumentApprovalData] = useState<DocumentApprovalData[]>([]);
    const [documentApprovalQueue, setDocumentApprovalQueue] = useState<DocumentApprovalData[]>([]);
    const [documentApprovalCount, setDocumentApprovalCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [selectedClient, setSelectedClient] = useState<number>();
    const [selectedTRM, setSelectedTRM] = useState<number>();
    const [selectedTrustAdvisor, setSelectedTrustAdvisor] = useState<number>();
    const [selectedTrustAdministrator, setSelectedTrustAdministrator] = useState<number>();
    const [selectedClientAdvisor, setSelectedClientAdvisor] = useState<number>();
    const [clientOptions, setClientOptions] = useState<DropdownItem[]>([]);
    const [trmOptions, setTRMOptions] = useState<DropdownItem[]>([]);
    const [trustAdvisorOptions, setTrustAdvisorOptions] = useState<DropdownItem[]>([]);
    const [trustAdministratorOptions, setTrustAdministratorOptions] = useState<DropdownItem[]>([]);
    const [clientAdvisorOptions, setClientAdvisorOptions] = useState<DropdownItem[]>([]);
    const [unreadNotifications, setUnreadNotifications] = useState<number>(0);

    useEffect(() => {
        if (!appData) {
            setSelectedClient(undefined);
            setSelectedTRM(undefined);
            setSelectedTrustAdvisor(undefined);
            setSelectedTrustAdministrator(undefined);
            setSelectedClientAdvisor(undefined);
            setClientOptions([]);
            setTRMOptions([]);
            setTrustAdvisorOptions([]);
            setTrustAdministratorOptions([]);
            setClientAdvisorOptions([]);
            return;
        }
        (async () => {
            setLoading(true);
            try {
                setClientOptions(getPortfolioListOptions(appData));
                const promises = [
                    sproc("GetTrustData"),
                    sproc("GetPolicyLapseProjections"),
                    sproc("GetPolicies"),
                    sproc("GetClientDocumentApprovalRequests"),
                    sproc("GetTRMs"),
                    sproc("GetUnreadNotificationsCount"),
                    sproc("GetPoliciesWithMajorLoans"),
                    sproc("GetExpiringLevelTermPolicies"),
                    sproc("GetTrustAdvisors"),
                    sproc("GetUnmanagedTrustAdministrators"),
                    sproc("GetClientAdvisors")
                ];
                const [
                    trusts,
                    policyLapseProjections,
                    policies,
                    documentApprovals,
                    trms,
                    [notifications],
                    policyLoanData,
                    policyExpiringLevelTermData,
                    trustAdvisors,
                    trustAdministrators,
                    clientAdvisors
                ] = await Promise.all(promises);
                setTrustData(trusts);
                setPolicyData(policies);
                setPolicyLapseProjectionData(policyLapseProjections);
                setLapseProjectionSummary(createLapseProjectionSummary(policyLapseProjections));
                setTrustBreakdown(createTrustBreakdown(trusts));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setPolicyBreakdown(createPolicyBreakdown(policies, appData.organization.OrganizationID) as any);
                setDocumentApprovalData(
                    documentApprovals.map((doc, key: number) => ({ ...doc, isSelected: false, popupOpen: false, key }))
                );
                setDocumentApprovalQueue(
                    documentApprovals.map((doc, key: number) => ({ ...doc, isSelected: false, popupOpen: false, key }))
                );
                setDocumentApprovalCount(
                    documentApprovals.filter((doc: { ApprovalStatus: string }) => doc.ApprovalStatus === "Pending Approval").length
                );
                setTRMOptions(
                    trms.map((trm: { RelationshipManagerContactID: number; FileAs: string }) => ({
                        key: trm.RelationshipManagerContactID,
                        text: trm.FileAs,
                        value: trm.RelationshipManagerContactID
                    }))
                );
                setTrustAdvisorOptions(
                    trustAdvisors.map((r: { TrustAdvisorContactID: number; FileAs: string }) => ({
                        key: r.TrustAdvisorContactID,
                        text: r.FileAs,
                        value: r.TrustAdvisorContactID
                    }))
                );
                setTrustAdministratorOptions(
                    trustAdministrators.map((r: { AdministratorContactID: number; FileAs: string }) => ({
                        key: r.AdministratorContactID,
                        text: r.FileAs,
                        value: r.AdministratorContactID
                    }))
                );
                setClientAdvisorOptions(
                    clientAdvisors.map((r: { ClientAdvisorContactID: number; FileAs: string }) => ({
                        key: r.ClientAdvisorContactID,
                        text: r.FileAs,
                        value: r.ClientAdvisorContactID
                    }))
                );
                setUnreadNotifications(notifications.Count);
                setPolicyLoanData(policyLoanData);
                setMajorPolicyLoans(createMajorPolicyLoans(policyLoanData));
                setPolicyExpiringLevelTermData(policyExpiringLevelTermData);
                setExpiringLevelTermPolicies(createExpiringLevelTermPolicies(policyExpiringLevelTermData));
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [appData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setTrustBreakdown(
            createTrustBreakdown(
                filterSelector(
                    trustData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                )
            )
        );
        setLapseProjectionSummary(
            createLapseProjectionSummary(
                filterSelector(
                    policyLapseProjectionData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                )
            )
        );
        setTrustBreakdown(
            createTrustBreakdown(
                filterSelector(
                    trustData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                )
            )
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setPolicyBreakdown(
            createPolicyBreakdown(
                filterSelector(
                    policyData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                ),
                appData?.organization.OrganizationID
            ) as any
        );
        setDocumentApprovalQueue(
            filterSelector(
                documentApprovalData,
                selectedTRM,
                selectedClient,
                selectedTrustAdvisor,
                selectedTrustAdministrator,
                selectedClientAdvisor
            )
        );
        setDocumentApprovalCount(
            filterSelector(
                documentApprovalData,
                selectedTRM,
                selectedClient,
                selectedTrustAdvisor,
                selectedTrustAdministrator,
                selectedClientAdvisor
            ).filter((doc) => doc.ApprovalStatus === "Pending Approval").length
        );
        setExpiringLevelTermPolicies(
            createExpiringLevelTermPolicies(
                filterSelector(
                    policyExpiringLevelTermData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                )
            )
        );
        setMajorPolicyLoans(
            createMajorPolicyLoans(
                filterSelector(
                    policyLoanData,
                    selectedTRM,
                    selectedClient,
                    selectedTrustAdvisor,
                    selectedTrustAdministrator,
                    selectedClientAdvisor
                )
            )
        );
    }, [
        appData?.organization.OrganizationID,
        documentApprovalData,
        policyData,
        policyLapseProjectionData,
        selectedClient,
        selectedTRM,
        selectedClientAdvisor,
        selectedTrustAdministrator,
        selectedTrustAdvisor,
        trustData,
        policyExpiringLevelTermData,
        policyLoanData
    ]);

    const onDocumentsApprovalQueueChange = (docsToUpdate: DocumentApprovalData[]) => {
        const newApprovalData = documentApprovalData.map((doc) => {
            const changedDoc = docsToUpdate.find((d) => d.DocumentApprovalRequestID === doc.DocumentApprovalRequestID);
            if (changedDoc) {
                return changedDoc;
            }
            return doc;
        });
        setDocumentApprovalQueue(
            newApprovalData.sort((a, b) => new Date(a.DateOfNextAction).getTime() - new Date(b.DateOfNextAction).getTime())
        );
        setDocumentApprovalCount(newApprovalData.filter((doc) => doc.ApprovalStatus === "Pending Approval").length);
        setDocumentApprovalData(newApprovalData);
    };

    if (loading) {
        return <SimpleLoader />;
    }

    return (
        <PortfolioContext.Provider
            value={{
                trustData,
                policyData,
                policyLapseProjectionData,
                lapseProjectionSummary,
                policyBreakdown,
                trustBreakdown,
                documentApprovalQueue,
                documentApprovalCount,
                policyExpiringLevelTermData,
                expiringLevelTermPolicies,
                policyLoanData,
                majorPolicyLoans,
                loading,
                error,
                selectedClient,
                selectedTRM,
                selectedTrustAdvisor,
                selectedTrustAdministrator,
                selectedClientAdvisor,
                clientOptions,
                trmOptions,
                trustAdvisorOptions,
                trustAdministratorOptions,
                clientAdvisorOptions,
                setSelectedClient,
                setSelectedTRM,
                setSelectedTrustAdvisor,
                setSelectedTrustAdministrator,
                setSelectedClientAdvisor,
                onDocumentsApprovalQueueChange,
                setTrustData,
                setPolicyData,
                unreadNotifications,
                setUnreadNotifications
            }}
        >
            {children}
        </PortfolioContext.Provider>
    );
};

export interface PolicyData {
    PolicyID: number;
    IIQNumber: string;
    PolicyNumber: string;
    GrossDeathBenefit: number | null;
    NetDeathBenefit: number | null;
    TotalGrossCashValue: number | null;
    OutstandingLoanBalance: number | null;
    RiskRating: string | null;
    NextPremiumDueDate: string | null;
    TrustAccountNumber: string | null;
    ACPremiumStatus: string | null;
    PolicyStatus: string | null;
    PolicyCategory: string | null;
    PolicyType: string | null;
    CarrierID: number;
    CarrierGrade: string;
    CarrierName: string | null;
    ClientID: number;
    RelationshipManagerContactID: number | null;
    TrustID: number;
    TrustName: string;
    Active: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DocumentApprovalData = any;
